import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Image,
  message,
  Pagination,
  Popconfirm,
  Space,
  Table,
} from 'antd';
import type { TableColumnType } from 'antd';
import { batchOffline, getList } from '@/api/push';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { PageLink } from '@/config/pageConfig';

// 接口文档地址
// https://supptech.sg.larksuite.com/wiki/Nobsw0FhdilBpak9V2nlUc29gxh

export const CONTENT_REVIEW_IDS_KEY = 'content_review_ids';
export const CONTENT_REVIEW_INDEX_KEY = 'content_review_index';

import { StatusEnm, StatusMap } from './type';
import type { PushItem, PushListResponse, ISearchParams } from './type';
import { useRequest, useUpdateEffect } from 'ahooks';

const PushMng: React.FC = () => {
  const [form] =
    Form.useForm<
      Omit<ISearchParams, 'currentPage' | 'pageSize' | 'reviewStatus'>
    >();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const isMobile = useAtomValue(isMobileAtom);
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const formValues: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key === 'channelDictId') {
        formValues[key] = parseInt(value);
      } else {
        formValues[key] = value;
      }
    });
    form.setFieldsValue(formValues);
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
  }, [searchParams, form]);

  const { data, loading, refresh, run } = useRequest(async (page = 1) => {
    const params: ISearchParams = {
      currentPage: page,
      pageSize,
    };
    const resp = await getList(params);

    if (resp.status === 200 && resp.data.status === 200) {
      return resp.data.data;
    }

    return {
      records: [],
      total: 0,
      currentPage: page,
      pageSize,
    };
  });

  useUpdateEffect(() => {
    run(page);
  }, [page]);

  const columns: TableColumnType<PushItem>[] = [
    {
      title: 'id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '推送图预览',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      // width: '120px',
      render: (_: string, record) => {
        if (record.pushType === '2') return '-';
        return (
          <Image
            key={_}
            src={_}
            width={50}
            height={50}
            loading="lazy"
            placeholder={
              <div
                style={{
                  width: 50,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                }}
              >
                <span style={{ fontSize: '12px', color: '#999' }}>
                  Loading
                </span>
              </div>
            }
            style={{
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            preview={{
              src: _
            }}
          />
        );
      },
    },
    {
      title: '推送状态',
      dataIndex: 'status',
      key: 'status',
      render: (_) => StatusMap.get(_) as string,
    },
    {
      title: '发布时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (_: string) => {
        return _ || '-';
      },
    },
    {
      title: '推送链接',
      dataIndex: 'jumpContent',
      key: 'jumpContent',
      render: (_: string) => _ || '-',
    },
    {
      title: '操作人员',
      dataIndex: 'updateUser',
      key: 'updateUser',
      render: (_: string) => _ || '-',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            onClick={() => navigate(`${PageLink.PushMng}/detail/${record.id}`)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  const batchOfflineDisabled = useMemo(() => {
    if (!selectedKeys.length) return true;
    const status = StatusEnm[0] as keyof typeof StatusEnm;
    const improperRow = data?.records.filter(
      (item) =>
        selectedKeys.includes(String(item.id)) &&
        item.status === StatusEnm[status]
    );

    console.log(improperRow, 'improperRow');
    return !!improperRow?.length;
  }, [selectedKeys, data]);

  const handleBatchOffline = useCallback(async () => {
    const res = await batchOffline({
      idList: selectedKeys,
    });

    if (res.status !== 200) {
      return message.error(res.message);
    }

    refresh();
  }, [selectedKeys, refresh]);

  return (
    <div className="content-review-container">
      {contextHolder}
      <div className="table-container">
        <Space size="middle" className="table-header">
          <Button
            type="primary"
            onClick={() => navigate(`${PageLink.PushMng}/create`)}
          >
            创建
          </Button>
          <Popconfirm
            title="你确定要将这些内容下线吗?"
            onConfirm={handleBatchOffline}
            okType="danger"
          >
            <Button
              type="primary"
              danger
              disabled={batchOfflineDisabled}
              // icon={<CloseOutlined />}
            >
              批量下线
            </Button>
          </Popconfirm>
        </Space>
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          key="push"
          columns={columns}
          dataSource={data?.records || []}
          scroll={{ x: 'max-content' }}
          rowSelection={
            isMobile
              ? undefined
              : {
                  selectedRowKeys: selectedKeys,
                  type: 'checkbox',
                  fixed: true,
                  onChange: (selectedRowKeys) => {
                    setSelectedKeys(selectedRowKeys as string[]);
                  },
                }
          }
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>
    </div>
  );
};

export default PushMng;
